import { type SyncQueue } from "./SyncQueue";

export enum SendOutcome {
  Ok = "ok",
  ClientError = "clientError",
  Transient = "transient",
}

export type SendResult<TResult> =
  | { outcome: SendOutcome.Ok; result: TResult }
  | { outcome: SendOutcome.ClientError }
  | { outcome: SendOutcome.Transient };

export interface FlushOnceArgs<TPayload, TResult> {
  queue: SyncQueue<TPayload>;
  send: (entry: TPayload) => Promise<SendResult<TResult>>;
  onSuccess?: (result: TResult, sentEntry: TPayload) => void;
  // Checked AFTER the send resolves, before the queue is mutated. SyncQueue
  // is memory-authoritative and persists its whole array, so a flush whose
  // owner was stopped mid-send must NOT dequeue/persist: a successor queue
  // over the same storage key may have loaded and written newer entries in
  // the meantime, and committing the stale in-memory snapshot would wipe
  // them from storage. Declining leaves the sent head queued — the successor
  // re-sends it, which the server's LWW makes a no-op (same clientUpdatedAt
  // fails the strict > check).
  shouldCommit?: () => boolean;
}

export async function flushOnce<TPayload, TResult>({
  queue,
  send,
  onSuccess,
  shouldCommit,
}: FlushOnceArgs<TPayload, TResult>): Promise<SendOutcome | null> {
  const head = queue.peek();

  if (head == null) {
    return null;
  }

  const result = await send(head);

  if (result.outcome === SendOutcome.Transient) {
    return SendOutcome.Transient;
  }

  if (shouldCommit != null && !shouldCommit()) {
    // Declined commits report TRANSIENT, not the send's own outcome: the
    // head stays queued, and an Ok would make the worker see a non-empty
    // queue and schedule(0) — a tight resend loop for as long as the gate
    // stays false with the worker still running (an impersonation flip is
    // exactly that window until the React cleanup stops it). Transient
    // routes it through the normal exponential backoff instead.
    return SendOutcome.Transient;
  }

  // Only dequeue if the head we sent is still at the front of the queue.
  // If a coalescing enqueue happened during the in-flight send, the slot
  // at index 0 now holds a newer object with merged data, and dequeuing
  // it would drop the user's newer write. In that case leave the new
  // entry for the next tick — the server's LWW will keep the newest of
  // the two (already-sent old, about-to-send new) writes.
  const headStillOurs = queue.peek() === head;

  if (headStillOurs) {
    queue.dequeue();
  }

  // Skip the success callback when the head was coalesced. The older
  // send's result represents server state from before our newer pending
  // write was even sent; applying it would regress local UI behind the
  // data the user is about to ship. The next tick processes the newer
  // entry and reconciles to the authoritative state.
  if (headStillOurs && result.outcome === SendOutcome.Ok) {
    onSuccess?.(result.result, head);
  }

  return result.outcome;
}

export interface WorkerArgs<TPayload, TResult> extends FlushOnceArgs<
  TPayload,
  TResult
> {
  isOnline: () => boolean;
  intervalMs?: number;
  maxBackoffMs?: number;
}

export class SyncWorker<TPayload, TResult> {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private attempts = 0;
  private running = false;
  // Serializes ticks so a kick() that fires while a flushOnce is in flight
  // cannot spawn a second flushOnce. Without this, both would peek the
  // same head, dequeue it twice on completion, and silently drop the
  // next entry.
  private ticking = false;

  constructor(private readonly args: WorkerArgs<TPayload, TResult>) {}

  start(): void {
    this.running = true;
    this.schedule(0);
  }

  stop(): void {
    this.running = false;

    if (this.timer != null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  kick(): void {
    if (!this.running) {
      return;
    }

    if (this.ticking) {
      return;
    }

    this.schedule(0);
  }

  private schedule(delayMs: number): void {
    if (this.timer != null) {
      clearTimeout(this.timer);
    }

    this.timer = setTimeout(() => {
      void this.tick();
    }, delayMs);
  }

  private async tick(): Promise<void> {
    if (!this.running) {
      return;
    }

    if (this.ticking) {
      return;
    }

    this.ticking = true;

    try {
      // Re-assert our entries into shared storage before anything else: a
      // neighbouring tab's write can have dropped them since our last one. See
      // SyncQueue.reconcile for why this beats holding a lock.
      this.args.queue.reconcile();

      if (!this.args.isOnline() || this.args.queue.size() === 0) {
        this.schedule(this.args.intervalMs ?? 30_000);
        return;
      }

      // stop() only clears the timer — a tick already awaiting its send
      // keeps running. The commit guard keeps that orphaned flush from
      // mutating (and re-persisting) the queue after a successor instance
      // may have taken over the storage key.
      const outcome = await flushOnce({
        ...this.args,
        shouldCommit: () =>
          this.running && (this.args.shouldCommit?.() ?? true),
      });

      if (outcome === SendOutcome.Transient) {
        this.attempts += 1;
        const backoff = Math.min(
          this.args.maxBackoffMs ?? 60_000,
          1_000 * Math.pow(2, this.attempts - 1),
        );
        this.schedule(backoff);
        return;
      }

      this.attempts = 0;
      this.schedule(
        this.args.queue.size() > 0 ? 0 : (this.args.intervalMs ?? 30_000),
      );
    } finally {
      this.ticking = false;
    }
  }
}
